import { OrgEntitlement } from "./user";
import type { PublishableTypes } from "./publish";

export type FieldType =
  | "string"
  | "boolean"
  | "number"
  | "date"
  | "array"
  | "string_array"
  | "entity_id_array";

export type FieldVisibility =
  | "details"
  | "table"
  | "activity"
  | "api"
  | "overview"
  | "advanced_search"
  | "app";

/**
 * Entity properties define what is shown in the site popup on the map when a location (project, substation, etc.) is clicked.
 * https://renewmap.atlassian.net/wiki/spaces/SD/pages/33947825/Database+configurability+26+08
 */
export interface FieldConfig {
  id: string;
  field_key: string;
  field_name: string;
  field_type: FieldType;
  field_icon: string | null;
  entity_type: string;
  always_visible: boolean;
  group_name: string;
  created: string;
  updated: string;
  rank: number;
  description: string | null;
  required_entitlements: OrgEntitlement[] | null;
  visibility: FieldVisibility[];
  unit?: string | null;
}

export type FieldConfigShort = Pick<
  FieldConfig,
  "field_key" | "field_name" | "entity_type" | "field_type"
>;

export type FieldValueWithType = {
  field_type: FieldType;
  value_string: string | null;
  value_number: number | null;
  value_date: string | null;
  value_array: number[] | null;
  value_string_array: string[] | null;
};

export interface FieldValueWithMetadata extends FieldValueWithType {
  id: string;
  field_name: string;
  entity_id: string;
  source_url: string;
  entity_type: string;
  value_link: string;
  created: string;
  updated: string;
}

export interface FieldTypeInfo {
  field_key: string;
  field_type: string;
}
export type FieldValue = number | string | Date | null | number[] | string[];

/**
 * Converge a field value columns to a string value that can be shown in product
 */
export const getFieldValue = (data: FieldValueWithType): FieldValue => {
  switch (data.field_type) {
    case "array":
      return data.value_array;
    case "date":
      return data.value_date;
    case "string":
      return data.value_string;
    case "number":
      return data.value_number;
    case "string_array":
      return data.value_string_array;
    case "entity_id_array":
      return data.value_string_array;
  }
  return "";
};

/**
 * field_key values for all system fields defined in field-defs.  These fields are read from entity tables or timeseries rather than field_values_v1.
 */
export const SYSTEM_FIELD_KEYS = new Set([
  // projects
  "watchlist_prj",
  "on_screen_prj",
  "project_name_prj",
  "technology_prj",
  "project_type_prj",
  "project_status_prj",
  "project_size_mw_prj",
  "storage_size_mwh_prj",
  "state_prj",
  "country_prj",
  "turbines_prj",
  "opennem_prj",
  "network_prj",
  "id_prj",
  "mlf_prj",
  "emissions_prj",
  "production_prj",
  "vwap_prj",
  "curtailment_prj",
  // infrastructure_v5
  "on_screen_net",
  "infrastructure_name_net",
  "infrastructure_status_net",
  "voltage_net",
  "network_net",
  "construction_type_net",
  "line_id_net",
  "source_text_net",
  "state_net",
  "country_net",
  "id_net",
  "headroom_net",
  "headroompct_net",
  "infrastructure_status_gas",
  "source_text_gas",
  // organisation
  "on_screen_org",
  "organisations_org",
  "roles_org",
  "project_count_org",
  "total_mw_org",
  "url_org",
  "id_org",
  // changelog
  "update_type_cl",
  "timestamp_cl",
  // substation
  "substation_status_sub",
  "voltage_sub",
] as const);

export type SystemFieldKey =
  typeof SYSTEM_FIELD_KEYS extends Set<infer T> ? T : never;

// Native table -> column -> field_key, for the subset of fields
// that are backed by a real column on an entity table
export const columnToFieldKey: Partial<
  Record<PublishableTypes, Record<string, SystemFieldKey>>
> = {
  projects: {
    project_name: "project_name_prj",
    technology: "technology_prj",
    project_type: "project_type_prj",
    project_status: "project_status_prj",
    project_size_mw: "project_size_mw_prj",
    storage_size_mwh: "storage_size_mwh_prj",
    state: "state_prj",
    turbines: "turbines_prj",
  },
  infrastructure_v5: {
    infrastructure_name: "infrastructure_name_net",
    infrastructure_status: "infrastructure_status_net",
    voltage: "voltage_net",
    network: "network_net",
    construction_type: "construction_type_net",
    line_id: "line_id_net",
    source_text: "source_text_net",
    state: "state_net",
    country: "country_net",
  },
  organisations: {
    url: "url_org",
  },
  substations_v1: {
    substation_status: "substation_status_sub",
    voltage: "voltage_sub",
  },
};

/**
 * Columns in draft.projects the extractinator is allowed to write via table_diffs.
 * Single source of truth shared by the data-worker apply validation and the tools UI.
 */
export const ALLOWED_PROJECT_COLUMNS = [
  "project_name",
  "information",
  "state",
  "technology",
  "project_size_mw",
  "storage_size_mwh",
  "turbines",
  "project_status",
];

/**
 * The draft.field_values_v1 column a value is stored in for a given field_type.
 * Mirrors the column selection in getFieldValue above.
 */
export const fieldTypeToValueColumn = (fieldType: FieldType): string => {
  switch (fieldType) {
    case "number":
      return "value_number";
    case "date":
      return "value_date";
    case "array":
      return "value_array";
    case "string_array":
    case "entity_id_array":
      return "value_string_array";
    case "string":
    case "boolean":
      return "value_string";
  }
};

/**
 * For the provided field value convert it to a string to display in the table
 */
export const getPrimitiveValue = (val: FieldValue): string | number | null => {
  if (typeof val === "string") {
    return val;
  }

  if (typeof val === "number") {
    return val;
  }

  if (val instanceof Date) {
    return new Date(val).toLocaleDateString();
  }

  if (Array.isArray(val)) {
    return val.join(", ");
  }

  return null;
};

export interface FieldMetadata {
  id: string;
  field_name: string;
  field_key: string;
  description: string;
  field_type: string;
  group_name: string;
  rank: number;
}

/**
 * When joined onto other entities (projects, network) the field values are converged into one column
 */
export interface ExpandedFieldValue {
  id: string;
  field_name: string;
  field_key: string;
  shapefile_key: string;
  value: FieldValue;
}

export type WithFields<T> = T & {
  fields?: ExpandedFieldValue[];
};
